import { useMutation, useQueryClient } from '@tanstack/react-query';
import { datasetApi, getDatasetRequestConfig } from '../api';
import {
  DeclaredColumn,
  toColumnNames,
  toDeclaredSchema,
} from '../columns/declaredSchema';
import { GET_DATASETS_QUERY_KEYS } from './useTable';

export const useCreateDataset = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: {
      name: string;
      metadata: { name?: string; description?: string };
      // Declared columns make imports validate against them instead of
      // inferring the columns from the data.
      columns: DeclaredColumn[];
    }) => {
      const datasetName = payload.name;
      const declared = payload.columns.length > 0;

      const { data: table } =
        await datasetApi.createDatasetApiV2DatasetsDatasetNamePost(
          datasetName,
          {
            metadata: {
              ...payload.metadata,
              // Only datasets managed in Rossum are created here; the others
              // come from integrations.
              manual: true,
              ...(declared
                ? { columnNames: toColumnNames(payload.columns) }
                : {}),
            },
            ...(declared
              ? {
                  schema: toDeclaredSchema(payload.columns),
                  schema_mode: 'validate',
                }
              : {}),
          },
          getDatasetRequestConfig()
        );

      return table;
    },
    {
      // Refetch the datasets list, even with no page showing it, before the
      // caller navigates to the new dataset, so its page does not read a list
      // without it.
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: GET_DATASETS_QUERY_KEYS,
          refetchType: 'all',
        }),
    }
  );
};
